import {
  Bookmark,
  Box as EngineBox,
  Checkbox as EngineCheckbox,
  CommentAnnotation,
  Column as EngineColumn,
  Expanded,
  Floating,
  Spacer,
  Radio as EngineRadio,
  Row as EngineRow,
  Select as EngineSelect,
  Text as EngineText,
  TextField,
  HiddenTextField
} from '@st/document-rendering'
import { range } from '@st/util/array'
import { ReactNode } from 'react'

export { Bookmark, Spacer }

export type Pad = {
  top: number
  bottom: number
  left: number
  right: number
}

export const Pad = {
  zero: { top: 0, left: 0, right: 0, bottom: 0 } as Pad,
  all: (v: number): Pad => ({ top: v, bottom: v, left: v, right: v }),
  horizontal: (v: number): Pad => ({ top: 0, bottom: 0, left: v, right: v }),
  only: (pad: Partial<Pad>): Pad => ({ ...Pad.zero, ...pad })
}

type Align = 'start' | 'center' | 'end'

type BoxProps = {
  children?: ReactNode
  width?: number
  height?: number
  minHeight?: number
  padding?: Pad
  borderColor?: string
  borderWidth?: number
  backgroundColor?: string
  hAlign?: Align
  vAlign?: Align
  role?: string
  'aria-label'?: string
  'aria-level'?: number
}

export function Box({
  children,
  width,
  height,
  minHeight,
  padding,
  borderColor,
  borderWidth,
  backgroundColor,
  hAlign,
  vAlign
}: BoxProps) {
  return (
    <EngineBox
      width={finiteDimension(width)}
      height={finiteDimension(height)}
      minHeight={minHeight}
      padding={padding}
      border={
        borderColor || borderWidth
          ? { color: borderColor ?? backgroundColor ?? '#000000', width: borderWidth ?? 0 }
          : undefined
      }
      backgroundColor={backgroundColor}
      alignment={alignment(hAlign, vAlign)}
    >
      {children}
    </EngineBox>
  )
}

export function Row({
  children,
  align,
  gap = 0,
  mainAxisSize
}: {
  children: ReactNode
  align?: Align
  gap?: number
  mainAxisSize?: 'max' | 'min'
  tag?: string
}) {
  return (
    <EngineRow crossAxisAlignment={align ?? 'start'} mainAxisSize={mainAxisSize} gap={gap}>
      {children}
    </EngineRow>
  )
}

export function Column({
  children,
  align,
  gap = 0,
  mainAxisSize
}: {
  children: ReactNode
  align?: Align
  gap?: number
  mainAxisSize?: 'max' | 'min'
  tag?: string
}) {
  return (
    <EngineColumn crossAxisAlignment={align ?? 'start'} mainAxisSize={mainAxisSize} gap={gap}>
      {children}
    </EngineColumn>
  )
}

export function Expand({ children, flex = 1 }: { children: ReactNode; flex?: number }) {
  return <Expanded flex={flex}>{children}</Expanded>
}

export function Space({ w = 0, h = 0 }: { w?: number; h?: number }) {
  return <Box width={w} height={h} />
}

export function Text({
  children,
  fontSize,
  fontWeight = 'normal',
  fontStyle = 'normal',
  color,
  lineThrough,
  softWrap
}: {
  children: string
  fontSize: number
  fontWeight?: 'normal' | 'bold'
  fontStyle?: 'normal' | 'italic'
  color?: string
  overflow?: 'ellipsis'
  lineThrough?: boolean
  softWrap?: boolean
}) {
  return (
    <EngineText
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontStyle={fontStyle}
      color={color}
      lineThrough={lineThrough}
      softWrap={softWrap}
    >
      {children}
    </EngineText>
  )
}

export function TextInput({
  name,
  value,
  fontSize,
  multiline,
  mask,
  backgroundColor,
  borderColor,
  borderWidth,
  onChange
}: {
  name: string
  value: string
  fontSize?: number
  multiline?: boolean
  mask?: boolean
  backgroundColor?: string
  borderColor?: string
  borderWidth?: number
  'aria-label'?: string
  onChange?: (value: string) => void
}) {
  return (
    <TextField
      name={name}
      value={value}
      fontSize={fontSize}
      multiline={multiline}
      maskChar={mask ? '●' : undefined}
      backgroundColor={backgroundColor}
      border={
        borderColor || borderWidth
          ? { color: borderColor ?? '#cdcdcc', width: borderWidth ?? 0 }
          : undefined
      }
      onChange={onChange}
    />
  )
}

export function HiddenInput({
  name,
  value,
  onChange
}: {
  name: string
  value: string
  onChange?: (value: string) => void
}) {
  return <HiddenTextField name={name} value={value} onChange={onChange} />
}

export function Checkbox({
  name,
  value,
  backgroundColor,
  onChange
}: {
  name: string
  value: boolean
  backgroundColor?: string
  'aria-label'?: string
  onChange?: (value: boolean) => void
}) {
  return (
    <EngineCheckbox
      name={name}
      checked={value}
      backgroundColor={backgroundColor}
      border={{ color: '#111827', width: 1, radius: 1 }}
      onChange={onChange}
    />
  )
}

export function Radio({
  name,
  value,
  selected,
  backgroundColor,
  onChange
}: {
  name: string
  value: string | boolean | number
  selected: boolean
  backgroundColor?: string
  'aria-label'?: string
  onChange?: (value: boolean) => void
}) {
  return (
    <EngineRadio
      name={name}
      value={value}
      selected={selected}
      backgroundColor={backgroundColor}
      onChange={onChange}
    />
  )
}

export function Select({
  name,
  value,
  options,
  onChange
}: {
  name: string
  value?: string
  placeholder?: string
  'aria-label'?: string
  options: Array<{ value: string; label: string }>
  onChange?: (value: string) => void
}) {
  return (
    <EngineSelect
      name={name}
      value={value}
      options={options}
      border={{ color: '#cdcdcc', width: 0.5 }}
      backgroundColor="#ffffff"
      onChange={onChange}
    />
  )
}

export function Note({
  id,
  author,
  time,
  body,
  color = '#FFCD46'
}: {
  id: string
  author?: string
  time?: string
  body: string
  color?: string
}) {
  return (
    <Floating
      placement="right-start"
      offset={4}
      floating={
        <CommentAnnotation id={id} author={author} timestamp={time} content={body} color={color} />
      }
    >
      <Box width={0} height={0} />
    </Floating>
  )
}

type TableProps = {
  rowCount: number
  colCount: number
  colFlex: (colIndex: number) => number
  rowHeight: (rowIndex: number) => number
  children: (rowIndex: number, colIndex: number) => ReactNode
}

export function Table({ colCount, rowCount, rowHeight, colFlex, children }: TableProps) {
  return (
    <Row>
      {range(0, colCount - 1).map((colIndex) => (
        <Expand key={colIndex} flex={colFlex(colIndex)}>
          <Column mainAxisSize="min">
            {range(0, rowCount - 1).map((rowIndex) => {
              return (
                <Box key={rowIndex} height={rowHeight(rowIndex)} width={Infinity}>
                  {children(rowIndex, colIndex)}
                </Box>
              )
            })}
          </Column>
        </Expand>
      ))}
    </Row>
  )
}

function finiteDimension(value: number | undefined) {
  return value == Infinity ? undefined : value
}

function alignment(hAlign?: Align, vAlign?: Align) {
  if (!hAlign && !vAlign) return undefined
  const x = alignCoordinate(hAlign ?? 'start')
  const y = alignCoordinate(vAlign ?? 'start')
  return { x, y }
}

function alignCoordinate(align: Align) {
  switch (align) {
    case 'start':
      return -1
    case 'center':
      return 0
    case 'end':
      return 1
  }
}
